import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { CareerPathway, PathwayCard } from '@cpd-app/generated';
import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { ErrorCodeType } from '@cpd-app/shared-module/constants';
import {
  OccupationQuickMatchModel,
  OccupationProfileModel
} from '@cpd-app/core-module/models';
import { SkillsSurveyStepRequests } from './skills-survey-step-requests.service';

/**
 * Where a sub-occupation's display name is kept so a reload can still head the
 * page. Shared with `occupationDetailsResolver`, which is the reader: the
 * server names a sub-occupation from the title the client passes it, so on a
 * reload there is nothing to pass unless it was kept here.
 */
export const jobTitleStorageKey = (jobId: string | number): string =>
  `jobTitle:${jobId}`;

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private directLink: string;
  /** RELEASE-1. Set once the maintenance page has been shown; see goToErrorPage. */
  private isShowingMaintenance = false;

  constructor(
    private router: Router,
    // GR.6. Optional, so a service built by hand (specs) still works.
    @Optional() private surveyStepRequests?: SkillsSurveyStepRequests
  ) {}

  public goToAcceptTermsPage(): void {
    this.router.navigate([APP_ROUTES.AcceptTerms]);
  }

  public goToProfileWorkTypePage(): void {
    this.router.navigate([APP_ROUTES.ProfileWorkType]);
  }

  public goToHomePage(): void {
    this.router.navigate([APP_ROUTES.Home]);
  }

  public goToErrorPage(code: string): void {
    // The public portfolio page is never navigated away from. Spec 07: it is
    // unauthenticated, it makes exactly one query of its own, and every other
    // request the app fires on load is a member request that a visitor has no
    // session for. Letting one of those redirect would replace a perfectly
    // good published page with a member-facing error screen, in front of the
    // employer the member sent the link to.
    // window.location, not router.url: the requests that would redirect are
    // fired during bootstrap, before the first navigation has resolved, and
    // router.url is still "/" at that point.
    if (
      typeof window !== 'undefined' &&
      window.location?.pathname?.startsWith(`${APP_ROUTES.PublicPage}/`)
    ) {
      return;
    }

    /*
     * RELEASE-1. Maintenance wins and stays. In maintenance every request on
     * the page fails together, and the session and data failures behind the
     * one that says "maintenance" used to move visitors on to /error/403
     * (release night, 29 Oct). A reload starts over, which is how a visitor
     * gets back in once the site reopens.
     */
    if (this.isShowingMaintenance) {
      return;
    }
    if (code === ErrorCodeType.Maintenance) {
      this.isShowingMaintenance = true;
    }

    this.router.navigate([APP_ROUTES.Error + '/' + code]);
  }

  public goToQuickMatchPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.QuickMatch}`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}`;
    this.router.navigate([url]);
  }

  /**
   * `skipPrefilled` asks the skills wizard to open on the first step that still
   * needs an answer rather than step 1. Only the goal question sets it, and only
   * for a learner who has confirmed a transcript upload -- so a deliberate
   * retake still walks every step, the same as for anyone else.
   */
  /**
   * Starting the skills survey, from anywhere outside it.
   *
   * 03-skills-survey. Separate from `goToBuildYourTargetSkillsPage` on
   * purpose: that one is how the OLD wizard moves between its own steps, and
   * several of its steps call it, so repointing it would drop somebody
   * mid-wizard into the redesigned survey. This is the way IN, and it is what
   * every "Take Skills Survey" call to action should use.
   */
  public goToSkillsSurvey(): void {
    this.router.navigate([APP_ROUTES.CareerPlanning.SkillsSurvey]);
  }

  /**
   * GR.6. The Skills Survey opened at its goals step, where a career goal is
   * picked. This replaces `goToBuildYourTargetCareerGoalsPage` for every way
   * in from the current product: that page is the legacy wizard. The survey
   * honors the step only when the steps before it are answered.
   */
  public goToSkillsSurveyGoalsStep(): void {
    this.router.navigate([APP_ROUTES.CareerPlanning.SkillsSurvey], {
      queryParams: { step: 'goals' }
    });
    /*
     * Already on the survey, the address may already say `step=goals`, and
     * then the router does nothing. Ask the open survey directly; when the
     * address did change as well, the second ask lands on the step the
     * survey is already on and moves nothing.
     */
    if (
      this.router.url?.split('?')[0] === APP_ROUTES.CareerPlanning.SkillsSurvey
    ) {
      this.surveyStepRequests?.request('goals');
    }
  }

  public goToBuildYourTargetSkillsPage(
    backButton = false,
    skipPrefilled = false
  ): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/skills`;
    this.router.navigate([url], {
      state: { backButton, skipPrefilled }
    });
  }

  /**
   * The LEGACY wizard's career-goals page. Only the legacy wizard's own steps
   * call this now; a way in from the current product uses
   * `goToSkillsSurveyGoalsStep` (GR.6).
   */
  public goToBuildYourTargetCareerGoalsPage(backButton = false): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/career-goals`;
    this.router.navigate([url], {
      state: { backButton }
    });
  }

  public goToBuildYourTargetOtherSkillsPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/other-skills`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetCareerGoalsEducationPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/education-goals`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetEducationPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/education`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetInterestsPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/interests`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetValuesPage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/values`;
    this.router.navigate([url]);
  }

  public goToBuildYourTargetLifestylePage(): void {
    const url = `${APP_ROUTES.CareerPlanning.BuildTarget}/lifestyle`;
    this.router.navigate([url]);
  }

  public goToGoalsReport(): void {
    const url = `${APP_ROUTES.GoalsReport}`;
    if (this.router.url.includes(url)) {
      return;
    }
    this.router.navigate([url]);
  }

  public goToOccupationDetailPage(
    occupation: {
      onetCode: string;
      jobId?: number;
      jobTitle?: string;
      isPathway?: boolean;
    },
    fromPage: 'quickMatch' | 'buildYourTarget' = 'quickMatch'
  ): void {
    const url = this.getOccupationDetailRootUrl(occupation);

    /*
     * Keyed by jobId, and only when there is something to key.
     *
     * This used to write one global 'jobTitle'. A sub-occupation page takes its
     * heading from this value on a reload, so the single key meant reloading
     * sub-occupation B after having visited A put A's name on B's page. And
     * `setItem` stringifies, so an occupation with no jobTitle stored the
     * literal "undefined" and the page was headed "undefined".
     */
    if (occupation.jobId && occupation.jobTitle) {
      localStorage.setItem(
        jobTitleStorageKey(occupation.jobId),
        occupation.jobTitle
      );
    }

    this.router.navigate([url], {
      state: {
        occupationReferrerPage: fromPage,
        jobTitle: occupation.jobTitle
      }
    });
  }

  public goToOccupationDetailPageAlt(
    onetCode: string,
    jobId: number = null,
    fromPage: 'quickMatch' | 'buildYourTarget' = 'quickMatch'
  ): void {
    const url = this.getOccupationDetailRootUrlAlt(onetCode, jobId);
    this.router.navigate([url], {
      state: { occupationReferrerPage: fromPage }
    });
  }

  public goToOccupationProfilePage(
    occupationProfileModel: OccupationProfileModel | OccupationQuickMatchModel,
    fromPage: 'quickMatch' | 'buildYourTarget' = 'quickMatch'
  ): void {
    const url = this.getOccupationProfilePageUrl(occupationProfileModel);
    this.router.navigate([url], {
      state: { occupationReferrerPage: fromPage }
    });
  }

  public goToPathwayPage(pathway: CareerPathway): void {
    const url = `${APP_ROUTES.Pathway}/${pathway.pathwayId}`;
    this.router.navigate([url]);
  }

  public goToPathwayPageFromPathwayCard(pathway: PathwayCard): void {
    const url = `${APP_ROUTES.Pathway}/${pathway.pathwayId}`;
    this.router.navigate([url]);
  }

  public goToSystemStatus(): void {
    const url = `${APP_ROUTES.About.SystemStatus}`;
    this.router.navigate([url]);
  }

  public goToManager(): void {
    const url = `${APP_ROUTES.Manager.Base}`;
    this.router.navigate([url]);
  }

  public goToAnalytics(): void {
    const url = `${APP_ROUTES.Analytics}`;
    this.router.navigate([url]);
  }

  public forgotPassword(): void {
    const url = `${APP_ROUTES.Account.ForgotPassword}`;
    this.router.navigate([url]);
  }

  public goToUrl(url: string): void {
    this.router.navigate([url]);
  }

  /**
   * STAFF-1. A URL recorded whole (the login wall's), query string and all.
   * goToUrl's navigate([url]) treats it as one path segment and encodes the
   * "?", which lands on the 404 page.
   */
  public goToStoredUrl(url: string): void {
    this.router.navigateByUrl(url);
  }

  public setDirectLink(link: string): void {
    if (
      !link.includes('error') &&
      link !== '/' &&
      link !== APP_ROUTES.ProfileWorkType
    ) {
      this.directLink = link;
    }
  }

  public goToDirectLink(): void {
    if (this.directLink && this.directLink !== APP_ROUTES.Passcode) {
      if (this.router.url.includes(this.directLink)) {
        return;
      }

      this.router.navigateByUrl(this.directLink);
      return;
    }

    this.goToHomePage();
  }

  private getOccupationDetailRootUrl(model: {
    onetCode: string;
    jobId?: number;
    jobTitle?: string;
    isPathway?: boolean;
  }): string {
    return model.isPathway
      ? `${APP_ROUTES.Occupation}/${model.onetCode}/sub-occupations/${model.jobId}`
      : `${APP_ROUTES.Occupation}/${model.onetCode}`;
  }

  private getOccupationDetailRootUrlAlt(
    onetCode: string,
    jobId: number
  ): string {
    return jobId
      ? `${APP_ROUTES.Occupation}/${onetCode}/sub-occupations/${jobId}`
      : `${APP_ROUTES.Occupation}/${onetCode}`;
  }

  private getOccupationProfilePageUrl(
    model: OccupationProfileModel | OccupationQuickMatchModel
  ): string {
    return `${APP_ROUTES.Occupation}/${model.onetCode}`;
  }
}
